import { computed, defineComponent, ref, watch } from '@vue/composition-api';
import { SelectInput } from '@/components/Inputs/SelectInput';
import { BASE_INPUT_STYLE } from '@/components/Inputs/_components/BaseInput';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { MAX_OFFER_YEARS_AHEAD } from '@/constants/offer-object';
import i18n from '@/i18n';
import { useHalfToDate } from '@/util/half-to-date';
const { useState } = createNamespacedHelpers('offer');
export default defineComponent({
    name: 'SelectShownYear',
    components: {
        SelectInput,
    },
    props: {
        modelValue: {
            type: Number,
            required: false,
        },
        showAllRange: Boolean,
    },
    emits: ['update:modelValue', 'change'],
    setup(props, { emit }) {
        const { currentOffer } = useState(['currentOffer']);
        const nowYear = new Date().getFullYear();
        const offerStartDate = computed(() => {
            const s = currentOffer.value?.startDate;
            if (typeof s !== 'string')
                return null;
            return useHalfToDate(s, false);
        });
        const offerEndDate = computed(() => {
            const e = currentOffer.value?.endDate;
            if (typeof e !== 'string')
                return null;
            return useHalfToDate(e, true);
        });
        const singleYear = computed(() => {
            if (!offerStartDate.value || !offerEndDate.value)
                return null;
            const startYear = offerStartDate.value.getFullYear();
            const endYear = offerEndDate.value.getFullYear();
            return startYear === endYear ? startYear : null;
        });
        const isSingleYearOffer = computed(() => singleYear.value !== null);
        const isIndefinite = computed(() => currentOffer.value?.isIndefinite === true);
        const baseYear = computed(() => {
            if (singleYear.value != null)
                return singleYear.value;
            return offerStartDate.value?.getFullYear() ?? nowYear;
        });
        const maxYear = computed(() => {
            if (singleYear.value != null)
                return singleYear.value;
            return (offerEndDate.value?.getFullYear() ??
                baseYear.value + MAX_OFFER_YEARS_AHEAD);
        });
        const allRangeOption = computed(() => ({
            // Ensure the label updates when the language changes.
            label: (() => {
                // eslint-disable-next-line no-unused-expressions -- make locale a reactive dependency
                i18n.locale;
                return i18n.t('offers-view.all-time').toString();
            })(),
            value: 0,
        }));
        const yearsOptions = computed(() => {
            const options = [];
            let start = baseYear.value;
            let end = maxYear.value;
            if (start > end)
                [start, end] = [end, start];
            for (let year = start; year <= end; year++) {
                options.push({ label: String(year), value: year });
            }
            return options;
        });
        const options = computed(() => {
            return props.showAllRange && !isSingleYearOffer.value
                ? [allRangeOption.value, ...yearsOptions.value]
                : yearsOptions.value;
        });
        const findByValue = (val) => {
            // If "All time" is selected (0), always keep it (also for indefinite offers).
            if (val === 0)
                return options.value.find((o) => o.value === 0);
            // For indefinite offers, default to current year when value is empty.
            if (isIndefinite.value && val == null) {
                return (options.value.find((o) => o.value === nowYear) ?? options.value[0]);
            }
            if (val == null)
                return options.value[0];
            return options.value.find((o) => o.value === val) ?? options.value[0];
        };
        const selectedOption = ref(findByValue(props.modelValue));
        // Re-create selected option object when options change (e.g. language switch),
        // so the displayed label updates.
        watch(options, () => {
            selectedOption.value = findByValue(selectedOption.value?.value);
        }, { immediate: true });
        watch(() => props.modelValue, (val) => {
            selectedOption.value = findByValue(val);
        });
        watch(isIndefinite, (indefinite) => {
            if (indefinite &&
                (selectedOption.value?.value === 0 ||
                    selectedOption.value?.value == null)) {
                const opt = options.value.find((o) => o.value === nowYear) ?? options.value[0];
                selectedOption.value = opt;
                emit('update:modelValue', nowYear);
                emit('change', nowYear);
            }
        }, { immediate: true });
        watch(singleYear, (year) => {
            if (year == null)
                return;
            const opt = options.value.find((o) => o.value === year) ?? options.value[0];
            selectedOption.value = opt;
            emit('update:modelValue', year);
            emit('change', year);
        }, { immediate: true });
        watch(selectedOption, (opt) => {
            const year = opt?.value ?? baseYear.value;
            emit('update:modelValue', year);
            emit('change', year);
        });
        return {
            options,
            selectedOption,
            isSingleYearOffer,
            BASE_INPUT_STYLE,
        };
    },
});
